import { unit } from '@ant-design/cssinjs';
import { resetComponent } from '../../style';
import { genStyleHooks, mergeToken } from '../../theme/internal';
// ============================== Shared ==============================
const genSharedDividerStyle = token => {
  const {
    componentCls,
    sizePaddingEdgeHorizontal,
    colorSplit,
    lineWidth,
    textPaddingInline,
    orientationMargin,
    verticalMarginInline
  } = token;
  return {
    [componentCls]: Object.assign(Object.assign({}, resetComponent(token)), {
      borderBlockStart: "".concat(unit(lineWidth), " solid ").concat(colorSplit),
      // vertical
      '&-vertical': {
        position: 'relative',
        top: '-0.06em',
        display: 'inline-block',
        height: '0.9em',
        marginInline: verticalMarginInline,
        marginBlock: 0,
        verticalAlign: 'middle',
        borderTop: 0,
        borderInlineStart: "".concat(unit(lineWidth), " solid ").concat(colorSplit)
      },
      '&-horizontal': {
        display: 'flex',
        clear: 'both',
        width: '100%',
        minWidth: '100%',
        // Fix https://github.com/ant-design/ant-design/issues/10914
        margin: "".concat(unit(token.dividerHorizontalGutterMargin), " 0")
      },
      ["&-horizontal".concat(componentCls, "-with-text")]: {
        display: 'flex',
        alignItems: 'center',
        margin: "".concat(unit(token.dividerHorizontalWithTextGutterMargin), " 0"),
        color: token.colorTextHeading,
        fontWeight: 500,
        fontSize: token.fontSizeLG,
        whiteSpace: 'nowrap',
        textAlign: 'center',
        borderBlockStart: "0 ".concat(colorSplit),
        '&::before, &::after': {
          position: 'relative',
          width: '50%',
          borderBlockStart: "".concat(unit(lineWidth), " solid transparent"),
          // Chrome not accept `inherit` in `border-top`
          borderBlockStartColor: 'inherit',
          borderBlockEnd: 0,
          transform: 'translateY(50%)',
          content: "''"
        }
      },
      ["&-horizontal".concat(componentCls, "-with-text-left")]: {
        '&::before': {
          width: "calc(".concat(orientationMargin, " * 100%)")
        },
        '&::after': {
          width: "calc(100% - ".concat(orientationMargin, " * 100%)")
        }
      },
      ["&-horizontal".concat(componentCls, "-with-text-right")]: {
        '&::before': {
          width: "calc(100% - ".concat(orientationMargin, " * 100%)")
        },
        '&::after': {
          width: "calc(".concat(orientationMargin, " * 100%)")
        }
      },
      ["".concat(componentCls, "-inner-text")]: {
        display: 'inline-block',
        paddingBlock: 0,
        paddingInline: textPaddingInline
      },
      '&-dashed': {
        background: 'none',
        borderColor: colorSplit,
        borderStyle: 'dashed',
        borderWidth: "".concat(unit(lineWidth), " 0 0")
      },
      ["&-horizontal".concat(componentCls, "-with-text").concat(componentCls, "-dashed")]: {
        '&::before, &::after': {
          borderStyle: 'dashed none none'
        }
      },
      ["&-vertical".concat(componentCls, "-dashed")]: {
        borderInlineStartWidth: lineWidth,
        borderInlineEnd: 0,
        borderBlockStart: 0,
        borderBlockEnd: 0
      },
      '&-dotted': {
        background: 'none',
        borderColor: colorSplit,
        borderStyle: 'dotted',
        borderWidth: "".concat(unit(lineWidth), " 0 0")
      },
      ["&-horizontal".concat(componentCls, "-with-text").concat(componentCls, "-dotted")]: {
        '&::before, &::after': {
          borderStyle: 'dotted none none'
        }
      },
      ["&-vertical".concat(componentCls, "-dotted")]: {
        borderInlineStartWidth: lineWidth,
        borderInlineEnd: 0,
        borderBlockStart: 0,
        borderBlockEnd: 0
      },
      ["&-plain".concat(componentCls, "-with-text")]: {
        color: token.colorText,
        fontWeight: 'normal',
        fontSize: token.fontSize
      },
      ["&-horizontal".concat(componentCls, "-with-text-left").concat(componentCls, "-no-default-orientation-margin-left")]: {
        '&::before': {
          width: 0
        },
        '&::after': {
          width: '100%'
        },
        ["".concat(componentCls, "-inner-text")]: {
          paddingInlineStart: sizePaddingEdgeHorizontal
        }
      },
      ["&-horizontal".concat(componentCls, "-with-text-right").concat(componentCls, "-no-default-orientation-margin-right")]: {
        '&::before': {
          width: '100%'
        },
        '&::after': {
          width: 0
        },
        ["".concat(componentCls, "-inner-text")]: {
          paddingInlineEnd: sizePaddingEdgeHorizontal
        }
      }
    })
  };
};
export const prepareComponentToken = token => ({
  textPaddingInline: '1em',
  orientationMargin: 0.05,
  verticalMarginInline: token.marginXS
});
// ============================== Export ==============================
export default genStyleHooks('Divider', token => {
  const dividerToken = mergeToken(token, {
    dividerHorizontalWithTextGutterMargin: token.margin,
    dividerHorizontalGutterMargin: token.marginLG,
    sizePaddingEdgeHorizontal: 0
  });
  return [genSharedDividerStyle(dividerToken)];
}, prepareComponentToken, {
  unitless: {
    orientationMargin: true
  }
});